export type DeviceTooltipLabels = {
  mac: string;
  group: string;
  job: string;
  battery: string;
  lastCommunication: string;
  lastLocalization: string;
  notAvailable: string;
  noGroup: string;
};

export const getDeviceTooltipLabels = (localizationService: { toLanguageString: (key: string, fallback: string) => string }): DeviceTooltipLabels => ({
  mac: localizationService.toLanguageString('map.tooltip_mac', 'MAC'),
  group: localizationService.toLanguageString('map.tooltip_group', 'Group'),
  job: localizationService.toLanguageString('map.tooltip_job', 'Job'),
  battery: localizationService.toLanguageString('map.tooltip_battery', 'Battery'),
  lastCommunication: localizationService.toLanguageString('map.tooltip_last_communication', 'Last communication'),
  lastLocalization: localizationService.toLanguageString('map.tooltip_last_localization', 'Last localization'),
  notAvailable: localizationService.toLanguageString('map.tooltip_not_available', 'N/A'),
  noGroup: localizationService.toLanguageString('map.noGroup', 'Not assigned'),
});

const DEFAULT_GROUP_COLOR = '#94a3a8';

const safeParseArray = (value: string | null): any[] => {
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeMac = (value: any): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const isBlank = (value: any) =>
  value == null || (typeof value === 'string' && (value.trim() === '' || value === 'null' || value === 'undefined'));

const escapeHtml = (value: any): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeColor = (value: any): string => {
  if (typeof value !== 'string') {
    return DEFAULT_GROUP_COLOR;
  }
  const trimmed = value.trim();
  return /^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\))$/i.test(trimmed)
    ? trimmed
    : DEFAULT_GROUP_COLOR;
};

/**
 * Merges the marker data with the freshest device record stored by the Maps page
 * (`devicesList` is kept in sync with both REST and MQTT updates).
 */
export const resolveDeviceTooltipData = (mac: any, fallback: any = {}): any => {
  const key = normalizeMac(mac);
  const match = key
    ? safeParseArray(localStorage.getItem('devicesList')).find(
      (device: any) => normalizeMac(device?.deviceMac ?? device?.mac ?? device?.DeviceMac) === key
    )
    : undefined;

  const merged = { ...(fallback ?? {}), ...(match ?? {}) };
  if (isBlank(merged.name) && !isBlank(fallback?.name)) {
    merged.name = fallback.name;
  }
  return merged;
};

const parseDeviceDate = (value: any): Date | null => {
  if (value == null || value === 0 || value === '0' || value === '-') {
    return null;
  }

  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    // Backend legacy format: dd/MM/yyyy HH:mm:ss expressed in UTC.
    const legacy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (legacy) {
      const [, day, month, year, hour = '0', minute = '0', second = '0'] = legacy;
      date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)));
    } else {
      date = new Date(trimmed);
    }
  }

  if (!date || Number.isNaN(date.getTime()) || date.getUTCFullYear() < 1971) {
    return null;
  }

  return date;
};

const formatDeviceDate = (value: any, languageId: string | null): string | null => {
  const date = parseDeviceDate(value);
  if (!date) {
    return null;
  }
  try {
    return date.toLocaleString(languageId || undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return date.toLocaleString();
  }
};

const resolveGroup = (device: any, noGroupLabel: string) => {
  const groupId = device?.groupId ?? device?.GroupId ?? device?.idGroup ?? device?.group?.id ?? null;
  const groups = safeParseArray(localStorage.getItem('groupsCheckboxByMap'));
  const storedGroup = groupId == null || groupId === -1 || groupId === '-1'
    ? undefined
    : groups.find((group: any) => group?.id != null && String(group.id) === String(groupId));

  const directName = device?.groupName ?? device?.GroupName ?? device?.group?.name;
  const name = !isBlank(directName) ? directName : (storedGroup?.name ?? noGroupLabel);
  const color = storedGroup?.color ?? device?.groupColor ?? device?.GroupColor ?? device?.color;

  return { name, color: sanitizeColor(color) };
};

const resolveBattery = (device: any): number | null => {
  const raw = device?.battery ?? device?.Battery;
  if (raw == null || raw === '') {
    return null;
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.round(Math.min(100, Math.max(0, value)));
};

export const buildDeviceTooltipHtml = (rawDevice: any, labels: DeviceTooltipLabels, languageId: string | null): string => {
  const device = rawDevice ?? {};
  const mac = device?.deviceMac ?? device?.mac ?? device?.DeviceMac ?? '';
  const name = device?.name ?? device?.Name;
  const title = !isBlank(name) ? name : mac;
  const group = resolveGroup(device, labels.noGroup);
  const battery = resolveBattery(device);
  const lastCommunication = formatDeviceDate(
    device?.dateTime ?? device?.Datetime ?? device?.DateTime ?? device?.localTime,
    languageId
  );
  const lastLocalization = formatDeviceDate(
    device?.lastPositionDate ?? device?.LastPositionDate ?? device?.lastPositionDatetime ?? device?.localLastPositionDate,
    languageId
  );

  const notAvailable = `<span class="device-tooltip__na">${escapeHtml(labels.notAvailable)}</span>`;

  // Hue 0 (red) -> 120 (green) so the label colour reflects the charge level.
  const batteryHtml = battery == null
    ? notAvailable
    : `<span class="device-tooltip__battery-value" style="color:hsl(${Math.round(battery * 1.2)}, 72%, 32%);">${battery}%</span>`;

  const jobId = device?.jobId ?? device?.JobId ?? device?.job?.id;
  const storedJob = jobId == null || Number(jobId) === -1 || Number(jobId) === 0
    ? undefined
    : safeParseArray(localStorage.getItem('jobsListMap')).find((job: any) => String(job?.id) === String(jobId));
  const directJobName = device?.jobName ?? device?.JobName ?? device?.job?.name;
  const jobName = !isBlank(directJobName) ? directJobName : storedJob?.name;
  const jobColor = device?.jobColor ?? device?.JobColor ?? device?.job?.color ?? storedJob?.color;
  const jobHtml = isBlank(jobName)
    ? ''
    : `<span class="device-tooltip__group">${isBlank(jobColor) ? '' : `<span class="device-tooltip__group-dot" style="background:${sanitizeColor(jobColor)};" aria-hidden="true"></span>`}${escapeHtml(jobName)}</span>`;

  const row = (label: string, value: string) =>
    `<div class="device-tooltip__row"><dt>${escapeHtml(label)}</dt><dd>${value}</dd></div>`;

  return `<div class="device-tooltip" style="--device-tooltip-accent:${group.color};">
    <div class="device-tooltip__header">
      <span class="device-tooltip__swatch" aria-hidden="true"></span>
      <span class="device-tooltip__title">${escapeHtml(title)}</span>
    </div>
    <dl class="device-tooltip__body">
      ${row(labels.mac, `<span class="device-tooltip__mono">${escapeHtml(mac) || notAvailable}</span>`)}
      ${row(labels.group, `<span class="device-tooltip__group"><span class="device-tooltip__group-dot" aria-hidden="true"></span>${escapeHtml(group.name)}</span>`)}
      ${jobHtml ? row(labels.job, jobHtml) : ''}
      ${row(labels.battery, batteryHtml)}
      ${row(labels.lastCommunication, lastCommunication ? escapeHtml(lastCommunication) : notAvailable)}
      ${row(labels.lastLocalization, lastLocalization ? escapeHtml(lastLocalization) : notAvailable)}
    </dl>
  </div>`;
};
